import '@fortawesome/fontawesome-svg-core/styles.css';
import UtmDataSource from './src/data/utm.datasource';
import VisitCountDataSource from './src/data/visit-count.datasource';
import SessionHistory from './src/utils/browser/session-history';
import { GlobalStyled, ThemeContainer } from '@web/atomic';
import { ApolloProvider } from '@web/data/apollo-client';
import messengerAuthClient from '@web/data/messenger-auth-client';
import nativeAuthClient from '@web/data/native-auth-client';
import { NativeHelper } from '@web/data/native.helper';
import { EventKind, sendEvent } from '@web/utils/analytics';
import { initDatadogWeb } from '@web/utils/datadog';
import 'microtip/microtip.css';
import QueryString from 'querystringify';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import { ThemeProvider } from 'styled-components';
import './src/assets/fonts/fonts.css';
import { ErrorBoundary } from './src/components/obj.error-boundary/error-boundary.component';

// Helper function to detect language from route path
const detectLanguageFromPath = (pathname: string): string => {
  if (pathname.startsWith('/en/') || pathname === '/en') return 'en';
  if (pathname.startsWith('/es/') || pathname === '/es') return 'es';
  return 'pt-BR';
};

// Datadog Browser RUM + Logs. Runs once, before the first render, browser only.
// https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/#onClientEntry
export const onClientEntry = () => {
  initDatadogWeb({
    service: 'vigilantes-do-sono-landing-page',
    version: process.env.GATSBY_APP_VERSION,
  });
};

export const wrapRootElement = ({ element }) => {
  // Detect language from current path (client-side only)
  const currentLang = typeof window !== 'undefined' ? detectLanguageFromPath(window.location.pathname) : 'pt-BR';

  return (
    <ErrorBoundary>
      <ThemeContainer>
        {(theme) => {
          return (
            <ThemeProvider theme={theme}>
              <>
                <Helmet>
                  <html lang={currentLang} />
                </Helmet>
                <ApolloProvider client={NativeHelper.isNative ? nativeAuthClient : messengerAuthClient}>{element}</ApolloProvider>
              </>
            </ThemeProvider>
          );
        }}
      </ThemeContainer>
    </ErrorBoundary>
  );
};

// https://github.com/gatsbyjs/gatsby/issues/20594#issuecomment-574262809
export const wrapPageElement = ({ element }) => (
  <>
    <GlobalStyled />
    {element}
  </>
);

export const onRouteUpdate = ({ location, prevLocation }) => {
  const search = QueryString.parse(location.search);

  // Handle lang query parameter redirection to route-based i18n
  if (search.lang && typeof window !== 'undefined') {
    const lang = search.lang;

    // Only redirect for supported languages
    if (lang === 'en' || lang === 'es') {
      // Check if we're not already on a language-prefixed route
      const currentPath = location.pathname;
      const alreadyHasLangPrefix = currentPath.startsWith('/en/') || currentPath.startsWith('/es/');

      if (!alreadyHasLangPrefix) {
        // Remove lang from query params
        delete search.lang;
        const newSearch = QueryString.stringify(search);

        // Build new path with language prefix, preserving the current path
        const newPath = `/${lang}${currentPath}${newSearch ? '?' + newSearch : ''}`;

        // Redirect to the new route-based URL
        window.location.replace(newPath);
        return;
      }
    }
  }

  UtmDataSource.source = search.utm_source;
  UtmDataSource.medium = search.utm_medium;
  SessionHistory.handleRouteChange(location);
  VisitCountDataSource.incrementVisitCount();
};

export const onServiceWorkerUpdateReady = () => {
  window.location.reload();
  console.log('onServiceWorkerUpdateReady');
  sendEvent(EventKind.reload, {
    event_category: 'ServiceWorkerUpdateReady',
    event_label: 'version update',
  });
};
